import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { BoxFull, Button, Dropdown, InputTextarea, TransactionalSubheader, TreeSelection } from "iuvity-ui-components";
import { Header } from "../../components";
import { CustomInput } from "../../components/inputs/CustomInput";
import { TenantCreatorSchema } from "../../validations/schemas";
import { AppContext } from "../../context";
import { ErrorMessage } from "@hookform/error-message";
import { FileUpload } from "primereact/fileupload";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { ColorPicker } from "primereact/colorpicker";
import { useNavigate } from "react-router-dom";
import { useTenants } from "../../hooks/useTenants";
import { CustomDialog, DialogProps } from "../../components/dialog/CustomDialog";
import { Tooltip } from 'primereact/tooltip';
import { TreeSelect } from 'primereact/treeselect';

interface NewTenantsProps {
  update?: boolean;
}

export const tenantCreatorDefaultValues = {
  name: "",
  parentid: "",
  alias: "",
  tenantcode: "",
  primarycolor: "",
  secondarycolor: "",
  logo: "",
  isotypelogo: "",
  logoname: "",
  isotypelogoname: ""
};

export const NewTenantPage: React.FC<NewTenantsProps> = ({ update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedTenant },
  } = useContext(AppContext);

  const { data: tenants } = useTenants("DATA");

  const [selectedParentId, setSelectedParentId] = useState(null);
  const [treeTenants, setTreeTenants] = useState<any>([]);


  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const { tenantAction } = useTenants("DATA");

  const [layerName, setLayerName] = useState<{
    logo: string,
    isotypelogo: string,
  }>({
    logo: '',
    isotypelogo: ''
  })
  const [layerNameValidator, setLayerNameValidator] = useState<{
    logo: string,
    isotypelogo: string,
  }>({
    logo: '',
    isotypelogo: ''
  })
  const [hasImageError, setHasImageError] = useState<{
    logo: boolean,
    isotypelogo: boolean,
  }>({
    logo: true,
    isotypelogo: true
  })
  const [disableUploadFile, setDisableUploadFile] = useState<{
    logo: boolean;
    isotypelogo: boolean;
  }>({
    logo: false,
    isotypelogo: false,
  })

  const invoiceUploadHandler = ({ files }: any, key: 'logo' | 'isotypelogo') => {
    setDisableUploadFile((prev: any) => ({
      ...prev,
      [key]: true,
    }));
    setValue(key, files[0], REVALIDATE_FORM_PROPS);
    const splitLayerName = files[0].name.split('.');
    const limit = 20;
    let fileName = files[0].name;
    // get both parts
    let fileNameExtension = "." + fileName.split(".").pop();
    const limitName = limit - fileNameExtension.length;
    let fileNameBase = fileName.substring(0, limit - fileNameExtension.length);
    
    fileName = fileNameBase + fileNameExtension;
    
    const formatLayerName = `${fileName}`;
    console.log(splitLayerName[0].slice(0, limitName), fileNameBase);
    setLayerNameValidator((prev) => ({
      ...prev,
      [key]: splitLayerName[0].slice(0, limitName)
    }));
    key === 'logo'
      ? setValue('logoname', splitLayerName[0].slice(0, limitName))
      : setValue('isotypelogoname', splitLayerName[0].slice(0, limitName));
    setLayerName((prev) => ({
      ...prev,
      [key]: formatLayerName
    }));
  }


  const invoiceRemoveHandler = (key: 'logo' | 'isotypelogo') => {
    setDisableUploadFile((prev: any) => ({
      ...prev,
      [key]: false,
    }));
    // @ts-ignore
    setValue(key, '', REVALIDATE_FORM_PROPS);
    key === 'logo'
      ? setValue('logoname', '', REVALIDATE_FORM_PROPS)
      : setValue('isotypelogoname', '', REVALIDATE_FORM_PROPS);
    setLayerName((prev) => ({
      ...prev,
      [key]: ''
    }));
    setLayerNameValidator((prev) => ({
      ...prev,
      [key]: ''
    }));
  }

  const logoValues = useWatch({
    control: control,
    name: "logo",
  });

  const isotypelogoValues = useWatch({
    control: control,
    name: "isotypelogo",
  });

  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onCreateTenant = async () => {
    const {
      alias, primarycolor, secondarycolor, logoname, isotypelogoname,
      ...rest
    } = getValues();

    const actionStatus = await tenantAction(update ? "UPDATE" : "CREATE",
      {
        ...rest,
        shortname: alias?.toUpperCase(),
        alias: alias?.toUpperCase(),
        primarycolor: `#${primarycolor}`,
        secondarycolor: `#${secondarycolor}`,
        logoName: logoname,
        isotypelogoName: isotypelogoname,
        [update ? "updatedby" : "createdby"]: user?.name,
        parentid: selectedParentId
      },
    );
    if (actionStatus === "SUCCESS") {
      navigate("/tenants");
      window.scrollTo(0, 0);
      reset(tenantCreatorDefaultValues);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Tenants.${update ? "save" : "create"}ModalTitle`),
          content: t(`Tenants.${update ? "save" : "create"}ModalContent`),
          onConfirm: onCreateTenant,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Tenants.cancelModalTitle"),
          content: t("Tenants.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (update && selectedTenant) {
      reset({
        id: selectedTenant.id,
        name: selectedTenant.name,
        alias: selectedTenant.shortname,
        primarycolor: selectedTenant.tenantbranding.primarycolor,
        secondarycolor: selectedTenant.tenantbranding.secondarycolor,
        logo: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0],
        isotypelogo: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0],
        logoname: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name,
        isotypelogoname: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name,
        status: selectedTenant.status,
      });
    } else {
      reset(tenantCreatorDefaultValues);
    }
  }, [reset, selectedTenant, update]);

  useEffect(() => {
    setHasImageError({
      logo: false,
      isotypelogo: false
    })
    if (layerNameValidator.logo !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator.logo))) {
      setHasImageError((prev) => ({
        ...prev,
        logo: true,
      }))
    }
    if (layerNameValidator.isotypelogo !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator.isotypelogo))) {
      setHasImageError((prev) => ({
        ...prev,
        isotypelogo: true,
      }))
    }
    layerNameValidator.logo === '' &&
      setHasImageError((prev) => ({
        ...prev,
        logo: true,
      }));
    layerNameValidator.isotypelogo === '' &&
      setHasImageError((prev) => ({
        ...prev,
        isotypelogo: true,
      }));
  }, [layerNameValidator])

  useEffect(() => {
    if (!tenants) return;
    if (tenants.length == 1) {
      const tenant = tenants[0];
      setTreeTenants([{
        id: tenant.id,
        key: tenant.id,
        label: tenant.name,
        data: tenant.id,
        parentid: tenant.parentid ?? null,
        children: []
      }
      ])
      return;
    }

    let tenantsData: any = tenants?.filter((tenant: any)=>
      tenant?.status !== 'DELETED')?.map((tenant: any) => {
        return {
          id: tenant.id,
          key: tenant.id,
          label: tenant.name,
          data: tenant.id,
          parentid: tenant.parentid ?? null,
          children: []
        };
    });


    // Map element ID to arr index
    const idMapping = tenantsData.reduce((acc: any, el: any, i: any) => {
      acc[el.id] = i;
      return acc;
    }, {});

    let root: any[] = [];
    // Push each element to parent's children array
    let hasOneRoot = false;
    tenantsData.forEach((el: any) => {
      // Handle the root element
      if (!hasOneRoot && (el.parentid == null || (el.parentid != null && tenantsData.filter((ten: any) => ten.parentid == el.id).length > 0))) {
        root = el;
        hasOneRoot = true;
        return;
      }
      // Use our mapping to locate the parent element in our data array
      const parentEl = tenantsData[idMapping[el.parentid]];
      if (!parentEl) return;
      // Add our current el to its parent's `children` array
      parentEl.children = [...(parentEl.children || []), el];
    });
    setTreeTenants([root])
  }, [tenants]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("Tenants.editTenantTitle") : t("Tenants.newTenantTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={`TransactionalSubheader.BreadCrumb.${t("Agents.agentsDatatableTitle")}`}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Tenants.advancedOptionsBreadcrumbLabel"),
              disabled: true
            },
            {
              label: t("Tenants.tenantsBreadcrumbLabel"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
                ? t("Tenants.editTenantTitle")
                : t("Tenants.newTenantTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull className="flex align-items-center justify-content-center pt-5 pb-0" variant="tertiary">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <p style={{ marginBottom: 'var(--space-base)' }}>
            {t("Tenants.createTenantSubtitle")}
          </p>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
                  <div
                    key={`c-${key}`}
                    className={`flex flex-column mb-0 px-6`}
                    style={{ width: "50%" }}
                  >
                    <Controller
                      key={key}
                      name={key as any}
                      control={control}
                      rules={(TenantCreatorSchema(t) as any)[key]}
                      render={({ field }) => {
                        if (["name", "alias", "tenantcode"].includes(key)) {
                          return <div style={{ paddingLeft: "3rem" }}>
                            <CustomInput
                              id={`input-tenant-${key}`}
                              className={
                                // @ts-ignore
                                errors[key]?.message
                                  ? "p-invalid"
                                  : idx !== arr.length - 1
                                    ? "mb-3"
                                    : ""
                              }
                              label={t(`Tenants.${key}Label`)}
                              placeholder={t(`Tenants.${key}Placeholder`)}
                              type="text"
                              autoComplete="off"
                              hasTooltip={key == 'tenantcode'}
                              tooltipText={t(`Tenants.${key}Tooltip`)}
                              style={{
                                width: '10rem !important',
                                top: '-100px !important',
                                textTransform: !!getValues('alias') && key === 'alias'
                                  ? 'uppercase' : undefined,
                              }}
                              keyfilter={
                                (TenantCreatorSchema(t) as any)[key]?.keyfilter
                              }
                              maxLength={
                                (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                              }
                              {...field}
                            />
                          </div>
                        }
                        if (key == 'parentid') {
                          return <div style={{ paddingLeft: "3rem" }}>
                            <label
                              htmlFor={`input-tenant-${key}`}
                              className={`mb-2 block lb}`}
                            >{t(`Tenants.parentLabel`)}
                              <span className={`tooltip-inputtext iu iu-icon-info tooltip-id-${key}`}
                                style={{ color: "var(--successColor)", marginLeft: "1rem", }} >
                                <Tooltip target={`.tooltip-id-${key}`} position="bottom">
                                  <p>{t(`Tenants.${key}Tooltip`)}</p>
                                </Tooltip>
                              </span>
                            </label>
                            <TreeSelect value={selectedParentId} filter selectionMode="single" id={`input-tenant-${key}`} name={key} options={treeTenants} onChange={(e: any) => setSelectedParentId(e.value)} panelClassName="select-tenant" className="md:w-20rem w-full" placeholder={t(`Tenants.parentPlaceholder`)}></TreeSelect>
                          </div>
                        }
                        return <></>;
                      }}
                    />
                    {
                      ['name', 'alias', 'tenantcode'].includes(key) &&
                      <ErrorMessage
                        errors={errors}
                        name={key as any}
                        //@ts-ignore
                        render={({ message }) => (
                          <small
                            key={`small-${key}`}
                            className="p-error block"
                            style={{
                              marginTop: "0.5rem",
                              marginBottom: "1.5rem",
                              marginLeft: "3rem",
                            }}
                          >
                            {message}
                          </small>
                        )}
                      />
                    }
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="colors_title">
            <span>{t("Tenants.customerColorsTitle")}</span>
          </div>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(tenantCreatorDefaultValues).filter((item) => item !== 'name' && item !== 'alias' && item !== 'tenantcode' && item !== 'parentid').map((key, idx, arr) => (
                  <div
                    key={`c-${key}`}
                    className={`flex flex-column mb-0 px-6`}
                    style={{ width: "50%" }}
                  >
                    <Controller
                      key={key}
                      name={key as any}
                      control={control}
                      rules={(TenantCreatorSchema(t) as any)[key]}
                      render={({ field }) => {
                        return (key === "primarycolor" || key === "secondarycolor") ? (
                          <div style={{ paddingLeft: "3rem" }}>
                            <>
                              {['primarycolor', 'secondarycolor'].includes(key) &&
                                <div className="mb-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                  <label htmlFor={`input-${key}`} className="block lb ">
                                    {t(`Tenants.${key}Label`)}
                                  </label>
                                  <div className="tenant-tooltip">
                                    <Tooltip target=".custom-target-icon" />
                                    <i
                                      className="custom-target-icon iu iu-icon-info"
                                      data-pr-tooltip={t(`Tenants.${key}Tooltip`)}
                                      data-pr-position="right"
                                      style={{ color: "var(--successColor)" }}
                                    />
                                  </div>
                                </div>
                              }
                              <div className={`${['primarycolor', 'secondarycolor'].includes(key) ? 'flex' : ''}`}>
                                {['primarycolor', 'secondarycolor'].includes(key) &&
                                  (
                                    <>
                                      <ColorPicker
                                        id="tenant-colorPicker"
                                        format="hex"
                                        value={getValues(key)}
                                        defaultColor="#cccccc"
                                        readOnly={true}
                                        style={{
                                          marginRight: "var(--marginLabelInput)",
                                        }}
                                        onClick={() => { }}
                                      />
                                      <p>#</p>
                                    </>
                                  )
                                }
                                <CustomInput
                                  id={`input-${key}`}
                                  className={
                                    // @ts-ignore
                                    errors[key]?.message
                                      ? "p-invalid"
                                      : idx !== arr.length - 1
                                        ? "mb-3"
                                        : ""
                                  }
                                  label={['primarycolor', 'secondarycolor'].includes(key) ? undefined : t(`Tenants.${key}Label`)}
                                  placeholder={t(`Tenants.${key}Placeholder`)}
                                  type="text"
                                  autoComplete="off"
                                  style={{
                                    width: '10rem !important',
                                  }}
                                  maxLength={
                                    (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                                  }
                                  {...field}
                                />
                              </div>
                            </>
                          </div>
                        ) : <></>
                      }}
                    />
                    {
                      ['primarycolor', 'secondarycolor'].includes(key) &&
                      <ErrorMessage
                        errors={errors}
                        name={key as any}
                        //@ts-ignore
                        render={({ message }) => (
                          <small
                            key={`small-${key}`}
                            className="p-error block"
                            style={{
                              marginTop: "0.5rem",
                              marginBottom: "1.5rem",
                              marginLeft: "3rem",
                            }}
                          >
                            {message}
                          </small>
                        )}
                      />
                    }
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="logotype_title">
            <span>{t("Tenants.customerLogoTitle")}</span>
            <Tooltip target=".custom-target-icon" />
            <i
              className="custom-target-icon iu iu-icon-info"
              data-pr-tooltip={t(`Tenants.uploadFileLogoTooltip`)}
              data-pr-position="right"
              style={{ color: "var(--successColor)", marginLeft: "0.5rem" }}
            />
          </div>
          <p style={{ margin: '0 2rem var(--space-base) 2rem' }}>{
            t("Tenants.customerLogoSubtitle")}
          </p>
          <div>
            {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
              <div
                key={`c-${key}`}
              >
                <Controller
                  key={key}
                  name={key as any}
                  control={control}
                  rules={(TenantCreatorSchema(t) as any)[key]}
                  render={({ field }) => {
                    return (key === "logo") ? (
                      <div className="card">
                        <FileUpload
                          accept="image/*"
                          // maxFileSize={1000000}
                          emptyTemplate={<></>}
                          cancelOptions={{
                            style: {
                              display: 'none',
                            }
                          }}
                          uploadOptions={{
                            style: {
                              display: 'none',
                            }
                          }}
                          chooseLabel={
                            t("PointsOfAttention.fileLabel")
                          }
                          chooseOptions={{
                            icon: 'iu iu-icon-upload',
                            className: disableUploadFile?.[key] ? ' p-disabled p-button-link' : 'p-button-link',
                          }}
                          onSelect={(e) => invoiceUploadHandler(e, key)}
                          onRemove={() => invoiceRemoveHandler(key)}
                        />
                      </div>
                    ) : <></>
                  }}
                />
                {
                  ['logo'].includes(key) &&
                    (hasImageError as any)[key]
                    ?
                    <small
                      key={`small-${key}`}
                      className="p-error block"
                      style={{
                        marginTop: "0.5rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      {
                        (layerNameValidator as any)[key] === ''
                          ? (<ErrorMessage
                            errors={errors}
                            name={key as any}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: idx === arr.length - 1 ? "" : "1.5rem",
                                }}
                              >
                                {message}
                              </small>
                            )}
                          />)
                          : t("GlobalConfigurations.generalErrorMessage")
                      }
                    </small>
                    : null
                }
              </div>
            ))}
          </div>

          <div className="logotype_title">
            <span>{t("Tenants.customerIsotypeTitle")}</span>
            <Tooltip target=".custom-target-icon" />
            <i
              className="custom-target-icon iu iu-icon-info"
              data-pr-tooltip={t(`Tenants.uploadFileIsotypeTooltip`)}
              data-pr-position="right"
              style={{ color: "var(--successColor)", marginLeft: "0.5rem" }}
            />
          </div>
          <div>
            {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
              <div
                key={`c-${key}`}
                className="mb-5"
              >
                <Controller
                  key={key}
                  name={key as any}
                  control={control}
                  rules={(TenantCreatorSchema(t) as any)[key]}
                  render={({ field }) => {
                    return (key === "isotypelogo") ? (
                      <div className="card">
                        <FileUpload
                          accept="image/*"
                          // maxFileSize={1000000}
                          emptyTemplate={<></>}
                          cancelOptions={{
                            style: {
                              display: 'none',
                            }
                          }}
                          uploadOptions={{
                            style: {
                              display: 'none',
                            }
                          }}
                          chooseLabel={
                            t("PointsOfAttention.fileLabel")
                          }
                          chooseOptions={{
                            icon: 'iu iu-icon-upload',
                            className: disableUploadFile?.[key] ? ' p-disabled p-button-link' : 'p-button-link',
                          }}
                          onSelect={(e) => invoiceUploadHandler(e, key)}
                          onRemove={() => invoiceRemoveHandler(key)}
                        />
                      </div>
                    ) : <></>
                  }}
                />
                {
                  ['isotypelogo'].includes(key) &&
                    (hasImageError as any)[key]
                    ?
                    <small
                      key={`small-${key}`}
                      className="p-error block"
                      style={{
                        marginTop: "0.5rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      {
                        (layerNameValidator as any)[key] === ''
                          ? (<ErrorMessage
                            errors={errors}
                            name={key as any}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: idx === arr.length - 1 ? "" : "1.5rem",
                                }}
                              >
                                {message}
                              </small>
                            )}
                          />)
                          : t("GlobalConfigurations.generalErrorMessage")
                      }
                    </small>
                    : null
                }
              </div>
            ))}
          </div>

        </div>
      </BoxFull>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            label={t("Tenants.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
            className="mr-4"
            variant="p-button-link"
          />
          <Button
            label={
              update ? t("Tenants.saveBtnLabel") : t("Tenants.createBtnLabel")
            }
            variant="p-button"
            onClick={() => handleModalProps("action")}
            disabled={
              update
                ? !(isValid && isDirty)
                : !(isValid && logoValues !== '' && isotypelogoValues !== '' && selectedParentId !== null && !hasImageError.logo && !hasImageError.isotypelogo)
            }
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
